import React, { useEffect, useMemo } from "react";
import { TabContext } from "@mui/lab";
import { Box, IconButton, InputAdornment, Stack, Tab } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { Bot, Colors, Direction, GridType, GridTypeEnum, RangeStart } from "../../../_app/types";
import { Order } from "../../../class/Order";
import { RootState } from "../../../store/store";
import { _createOrdersInvestmentStep } from "../../../utils/_createOrdersInvestmentStep";
import {
  calcOrderFieldsGridAmount,
  calcOrderFieldsPriceStep,
  calcPriceStep,
  printPriceStepRange,
} from "../../../utils/calcBotCreationFields";
import { setPriceStep } from "../../../store/slices/botSlice";
import CustomPaper from "../../../_components/CustomPaper";
import { TabsStyled } from "../../../_components/TabsStyled";
import SimpleFlex from "../../../components/ui/SimpleFlex";
import { TextFieldStyled } from "../../../_components/TextFieldStyled";
import { NumericFormatCustom } from "../../../components/ui/StyledAdron";
import MinusIcon from "../../../components/ui/icons/Minus";
import PlusIcon from "../../../components/ui/icons/Plus";
import DottedTextField from "../../../_components/DottedTextField";
import formatNumber from "../../../_utils/formatNumber";
import { precision } from "../../../utils/_precision";
import { TabPanelStyled } from "../../../_components/TabPanelStyled";
import { IconButtonStyled, NBSP } from "../../../_app/consts";
import AdronTypography from "../../../_components/AdronTypography";
import { _roundDec } from "../../../utils/_roundToDecimals";
import IconComponent from "../../../components/ui/IconComponent";

interface Props {
  gridsAmount: number;
  gridDirection: Direction;
  gridType: GridType;
  onChangeGridsAmount: (value: number) => void;
  onChangeGridType: (value: GridType) => void;
  priceStep: number;
  onChangePriceStep: (value: number) => void;
  bot: Bot;
  orderList: Order[];
  onChangeInvestmentInQuote: (investmentInQuote: number) => void;
  onChangeInvestmentInBase: (investmentInBase: number) => void;
  onChangeOrderList: (list: Order[]) => void;
  priceStepQuery: number;
  orderStep: number;
  rangeStartMode: RangeStart;
}

const FuturesBotCalcGrids = ({
  gridsAmount,
  onChangeGridsAmount,
  gridDirection,
  gridType,
  priceStep,
  onChangePriceStep,
  onChangeGridType,
  bot,
  orderList,
  onChangeInvestmentInQuote,
  onChangeInvestmentInBase,
  onChangeOrderList,
  priceStepQuery,
  orderStep,
  rangeStartMode,
}: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const CustomGridMode = useSelector((state: RootState) => state.customGridMode);

  const orderInvStep = useMemo(() => {
    return _createOrdersInvestmentStep(bot, orderStep, priceStepQuery);
  }, [bot, orderStep, priceStepQuery]);

  const { min, max } = useMemo(() => {
    const result = orderList.reduce<{ min: number; max: number }>(
      (acc, item) => {
        const value = gridType === "ARITHMETIC" ? item.priceStepPercent * 100 : item.priceStep;
        acc.min = Math.min(acc.min, value);
        acc.max = Math.max(acc.max, value);
        return acc;
      },
      { min: Infinity, max: -Infinity }
    );
    return result;
  }, [orderList]);

  const blockGridField = useMemo(() => {
    if (gridDirection === "Central" && gridsAmount <= 2) return true;
    if (gridDirection !== "Central" && gridsAmount <= 1) return true;
  }, [gridDirection, gridsAmount]);

  const rangePriceError = useMemo(() => {
    return bot.minPrice < 0 || bot.maxPrice < 0;
  }, [bot.minPrice, bot.maxPrice]);

  const maxPrice = useMemo(() => {
    const allPrices = bot.customGridSettings.flatMap((item) => [item.openPrice, item.closePrice]);
    return Math.max(...allPrices);
  }, [bot.customGridSettings]);

  const minPrice = useMemo(() => {
    const allPrices = bot.customGridSettings.flatMap((item) => [item.openPrice, item.closePrice]);
    return Math.min(...allPrices);
  }, [bot.customGridSettings]);

  const changeActiveTab = (event: React.SyntheticEvent, newValue: GridType) =>
    onChangeGridType(newValue);
  const handlePriceStep = (value: number) => {
    if (gridType === "ARITHMETIC") {
      onChangePriceStep(value);
    } else {
      onChangePriceStep(value);
    }
  };

  const handleIncreaseStep = () => {
    if (bot.gridDirection === "Central") {
      let increment = gridsAmount % 2 !== 0 ? 1 : 2;
      onChangeGridsAmount(gridsAmount + increment);
      // calcOrderFieldsGridAmount(gridsAmount + increment, dispatch, bot, rangeStartMode);
    } else {
      onChangeGridsAmount(gridsAmount + 1);
      // calcOrderFieldsGridAmount(gridsAmount + 1, dispatch, bot, rangeStartMode);
    }
  };

  const handleDecreaseStep = () => {
    if (bot.gridDirection === "Central") {
      let decrement = gridsAmount % 2 !== 0 ? 1 : 2;
      onChangeGridsAmount(gridsAmount - decrement); // 8
      // calcOrderFieldsGridAmount(gridsAmount - decrement, dispatch, bot, rangeStartMode);
    } else {
      onChangeGridsAmount(gridsAmount - 1);
      // calcOrderFieldsGridAmount(gridsAmount - 1, dispatch, bot, rangeStartMode);
    }
  };

  const increasePriceStep = () => {
    if (gridType === GridTypeEnum.Arithmetic) {
      onChangePriceStep(priceStep + priceStepQuery);
    } else {
      onChangePriceStep(priceStep + 0.01);
    }
    calcOrderFieldsPriceStep(bot.priceStep + priceStepQuery, dispatch, bot, rangeStartMode);
  };

  const decreasePriceStep = () => {
    if (gridType === GridTypeEnum.Arithmetic) {
      onChangePriceStep(priceStep - priceStepQuery);
    } else {
      onChangePriceStep(priceStep - 0.01);
    }
    calcOrderFieldsPriceStep(bot.priceStep, dispatch, bot, rangeStartMode);
  };

  useEffect(() => {
    if (!CustomGridMode) {
      calcOrderFieldsGridAmount(gridsAmount, dispatch, bot, rangeStartMode);
    }
    // else calcOrderFieldsGridAmount(gridsAmount, dispatch, bot, rangeStartMode);
  }, [gridsAmount]); // calc priceStep and range when changed gridsAmoount

  useEffect(() => {
    if (!CustomGridMode) {
      onChangeOrderList(orderInvStep.orders);
      onChangeInvestmentInBase(orderInvStep.stepInBase);
      onChangeInvestmentInQuote(orderInvStep.stepInQuote);

      if (rangeStartMode === "start") {
        // when changed priceStep - need calc new RangePrice
        let psTemp = 0;
        if (priceStep < 0) {
          onChangePriceStep(priceStep * -1);
          calcOrderFieldsPriceStep(priceStep * -1, dispatch, bot, rangeStartMode);
        } else {
          onChangePriceStep(priceStep);
          calcOrderFieldsPriceStep(priceStep, dispatch, bot, rangeStartMode);
        }
      }
    }
  }, [priceStep]);

  useEffect(() => {
    dispatch(
      setPriceStep(
        calcPriceStep(gridType, bot.minPrice, bot.maxPrice, gridsAmount, bot.ticker.priceStep)
      )
    );
  }, [gridType]);

  return (
    <CustomPaper>
      <TabContext value={gridType}>
        <TabsStyled style={{ borderRadius: 12 }} onChange={changeActiveTab}>
          <Tab
            disabled={CustomGridMode}
            label="Arithmetic"
            value={"ARITHMETIC"}
            className="AdronSecondaryTabs"
          />
          <Tab
            disabled={CustomGridMode}
            label="Geometric"
            value={"GEOMETRIC"}
            className="AdronSecondaryTabs"
          />
        </TabsStyled>
        <Stack direction={"column"} spacing={2} style={{ paddingTop: "16px" }}>
          <SimpleFlex gap={16}>
            <TextFieldStyled
              disabled={CustomGridMode}
              error={gridDirection === "Central" && gridsAmount % 2 !== 0}
              helperText={
                gridDirection === "Central" && gridsAmount % 2 !== 0
                  ? "This parameter must be even for Central direction"
                  : ""
              }
              fullWidth={true}
              label="Grid"
              variant="outlined"
              value={gridsAmount === 0 ? "" : gridsAmount}
              size="small"
              type={"number"}
              onChange={(e) => onChangeGridsAmount(+e.target.value <= 0 ? 1 : +e.target.value)}
              onBlur={() => calcOrderFieldsGridAmount(gridsAmount, dispatch, bot, rangeStartMode)}
              InputProps={{
                inputComponent: NumericFormatCustom as any,
                inputProps: {
                  inputMode: "numeric",
                  pattern: "[0-9]",
                  min: 1,
                  max: 50,
                },
              }}
            />
            <SimpleFlex gap={8}>
              <IconButton
                sx={{ opacity: blockGridField ? 0.4 : 1, ...IconButtonStyled(theme) }}
                disabled={CustomGridMode || blockGridField}
                onClick={handleDecreaseStep}
              >
                <MinusIcon />
              </IconButton>
              <IconButton
                sx={IconButtonStyled(theme)}
                disabled={CustomGridMode}
                onClick={handleIncreaseStep}
              >
                <PlusIcon />
              </IconButton>
            </SimpleFlex>
          </SimpleFlex>
          <Stack sx={{ width: "100%" }} direction={"column"} justifyContent={"center"} spacing={1}>
            {CustomGridMode ? (
              <DottedTextField
                placeholder={`Price step`}
                value={printPriceStepRange(bot.customGridSettings)}
              />
            ) : (
              <DottedTextField
                placeholder={`Price step`}
                value={
                  gridType === "ARITHMETIC" ? (
                    <>
                      {formatNumber(min, precision(bot.ticker.priceStep))}% &mdash;{" "}
                      {formatNumber(max, precision(bot.ticker.priceStep))}%
                    </>
                  ) : (
                    <>
                      ${formatNumber(min, 4)} &mdash; ${formatNumber(max, 4)}
                    </>
                  )
                }
              />
            )}
          </Stack>
        </Stack>
        <TabPanelStyled noPadding value="ARITHMETIC">
          <SimpleFlex gap={16}>
            <TextFieldStyled
              disabled={CustomGridMode || rangeStartMode === "range"}
              helperText={
                rangeStartMode === "range"
                  ? `Custom range is used, price step cannot be${NBSP}edited`
                  : ""
              }
              fullWidth={true}
              label="Step"
              variant="outlined"
              value={priceStep === 0 ? "" : priceStep}
              size="small"
              type={"text"}
              onChange={(e) => handlePriceStep(+e.target.value)}
              onBlur={() => calcOrderFieldsPriceStep(priceStep, dispatch, bot, rangeStartMode)}
              InputProps={{
                inputComponent: NumericFormatCustom as any,
                startAdornment: (
                  <InputAdornment position="start">
                    <AdronTypography value={`${gridType === "ARITHMETIC" ? "$" : "%"}`} />
                  </InputAdornment>
                ),
                inputProps: {
                  inputMode: "decimal",
                  pattern: "[0-9]*",
                  decimalCustom: Math.abs(Math.log10(bot.ticker.priceStep)),
                },
              }}
            />
            <SimpleFlex gap={8}>
              <IconButton
                sx={{ opacity: rangeStartMode === "range" ? 0.4 : 1, ...IconButtonStyled(theme) }}
                disabled={CustomGridMode || rangeStartMode === "range"}
                onClick={decreasePriceStep}
              >
                <MinusIcon />
              </IconButton>
              <IconButton
                sx={{ opacity: rangeStartMode === "range" ? 0.4 : 1, ...IconButtonStyled(theme) }}
                disabled={CustomGridMode || rangeStartMode === "range"}
                onClick={increasePriceStep}
              >
                <PlusIcon />
              </IconButton>
            </SimpleFlex>
          </SimpleFlex>
        </TabPanelStyled>
        <TabPanelStyled noPadding value="GEOMETRIC">
          <SimpleFlex gap={16}>
            <TextFieldStyled
              disabled={CustomGridMode || rangeStartMode === "range"}
              fullWidth={true}
              label="Step in %"
              variant="outlined"
              value={priceStep === 0 ? "" : priceStep * 100}
              onBlur={() =>
                calcOrderFieldsPriceStep(_roundDec(priceStep, 4), dispatch, bot, rangeStartMode)
              }
              size="small"
              onChange={(e) => handlePriceStep(+e.target.value / 100)}
              InputProps={{
                inputComponent: NumericFormatCustom as any,
                inputProps: {
                  inputMode: "decimal",
                  pattern: "[0-9]*",
                  decimalCustom: 2,
                },
                inputMode: "numeric",
              }}
            />
            <SimpleFlex gap={8}>
              <IconButton
                sx={IconButtonStyled(theme)}
                disabled={rangeStartMode === "range"}
                onClick={decreasePriceStep}
              >
                <MinusIcon />
              </IconButton>
              <IconButton
                sx={IconButtonStyled(theme)}
                disabled={rangeStartMode === "range"}
                onClick={increasePriceStep}
              >
                <PlusIcon />
              </IconButton>
            </SimpleFlex>
          </SimpleFlex>
        </TabPanelStyled>
        <Stack sx={{ marginTop: "16px" }}>
          {CustomGridMode ? (
            <DottedTextField
              placeholder={`Range price`}
              value={
                <>
                  {formatNumber(minPrice, precision(bot.ticker.priceStep))} {bot.quote} &mdash;{" "}
                  {formatNumber(maxPrice, precision(bot.ticker.priceStep))} {bot.quote}
                </>
              }
            />
          ) : (
            <DottedTextField
              placeholder={`Range price`}
              value={`${formatNumber(bot.minPrice, precision(bot.ticker.priceStep))} ${bot.quote}
              - ${formatNumber(bot.maxPrice, precision(bot.ticker.priceStep))} ${bot.quote}`}
            />
          )}
        </Stack>
      </TabContext>
    </CustomPaper>
  );
};

export default FuturesBotCalcGrids;
